import { useEffect, useRef, useState } from "react";
import { CheckIcon, ChevronsUpDownIcon, X } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

type SupplierResponse = components["schemas"]["SupplierResponse"];

const SUPPLIER_FETCH_LIMIT = 200;

export interface SupplierMultiSelectProps {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}

/** Empty selection means "all suppliers" (a global policy). */
export function SupplierMultiSelect({
  value,
  onChange,
  disabled = false,
  placeholder = "All suppliers",
}: SupplierMultiSelectProps) {
  const api = useDolfinClient();
  const [open, setOpen] = useState(false);
  const [suppliers, setSuppliers] = useState<SupplierResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    setLoading(true);
    api
      .GET("/v1/suppliers", { params: { query: { Limit: SUPPLIER_FETCH_LIMIT } } })
      .then(({ data }) => setSuppliers(data?.data ?? []))
      .catch(() => setSuppliers([]))
      .finally(() => setLoading(false));
  }, [api]);

  const selectedSet = new Set(value);
  const selectedSuppliers = value.map(
    (id) => suppliers.find((s) => s.id === id) ?? ({ id, name: id } as SupplierResponse),
  );

  function toggle(supplierId: string) {
    if (selectedSet.has(supplierId)) {
      onChange(value.filter((id) => id !== supplierId));
    } else {
      onChange([...value, supplierId]);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="h-auto min-h-9 w-full justify-between font-normal"
        >
          <div className="flex flex-wrap gap-1 py-0.5">
            {selectedSuppliers.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              selectedSuppliers.map((s) => (
                <Badge
                  key={s.id}
                  variant="secondary"
                  className="gap-1 pr-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span>{s.name}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${s.name}`}
                    className="rounded-full p-0.5 hover:bg-muted-foreground/20"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (s.id) toggle(s.id);
                    }}
                  >
                    <X className="size-3" />
                  </button>
                </Badge>
              ))
            )}
          </div>
          <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command>
          <CommandInput placeholder="Search suppliers..." />
          <CommandList>
            <CommandEmpty>{loading ? "Loading suppliers..." : "No suppliers found."}</CommandEmpty>
            <CommandGroup>
              {suppliers.map((s) => {
                if (!s.id) return null;
                const isSelected = selectedSet.has(s.id);
                return (
                  <CommandItem
                    key={s.id}
                    value={`${s.name} ${s.email ?? ""}`}
                    onSelect={() => toggle(s.id!)}
                  >
                    <CheckIcon className={cn("mr-2 size-4", isSelected ? "opacity-100" : "opacity-0")} />
                    <span className="flex flex-col">
                      <span>{s.name}</span>
                      {s.email && <span className="text-xs text-muted-foreground">{s.email}</span>}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {suppliers.length === SUPPLIER_FETCH_LIMIT && (
              <p className="px-2 py-1.5 text-xs text-muted-foreground border-t">
                Showing first {SUPPLIER_FETCH_LIMIT} suppliers. Refine search to narrow results.
              </p>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
